import React, { useState, useEffect } from 'react';
import { PageSection, SiteData, YachtRoute } from '../../../types';
import { dbService } from '../../../services/firebase';
import { Compass, Calendar, MapPin, Anchor, ArrowRight } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
}

export const YachtRoutesSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'POPÜLER SEYİR ROTALARI';
  const title = content.title || 'Ege ve Akdeniz’in Büyüleyici Mavi Yolculuk Rotaları';
  const description =
    content.description ||
    'Turkuaz koylardan antik limanlara, özenle planlanmış haftalık seyir programlarımızla rüya gibi bir tatil yaşayın.';

  const [routes, setRoutes] = useState<YachtRoute[]>([]);

  useEffect(() => {
    let isMounted = true;
    const fetchRoutes = async () => {
      try {
        const data = await dbService.getRoutes(siteData.id);
        if (isMounted) {
          setRoutes(data || []);
        }
      } catch (err) {
        console.warn('Routes load fallback:', err);
      }
    };
    fetchRoutes();
    return () => {
      isMounted = false;
    };
  }, [siteData.id]);

  const bgColor = style.backgroundColor || '#FFFFFF';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#0284C7';

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8 select-none"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          {badge && (
            <div
              className={`inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-500/10 text-cyan-700'
              }`}
            >
              {badge}
            </div>
          )}
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm sm:text-base leading-relaxed max-w-2xl mx-auto" style={{ color: mutedTextColor }}>
              {description}
            </p>
          )}
        </div>

        {/* Routes Grid */}
        {routes.length === 0 ? (
          <div className="text-center py-12 px-4 rounded-3xl border border-dashed border-slate-500/20 text-slate-400 max-w-xl mx-auto">
            <Compass className="w-8 h-8 mx-auto mb-2 opacity-50 text-cyan-500" />
            <p className="text-sm font-medium">Henüz yayınlanmış bir seyir rotası bulunmuyor.</p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {routes.map((route) => (
            <div
              key={route.id}
              className={`rounded-3xl overflow-hidden border shadow-md hover:shadow-xl transition duration-300 flex flex-col justify-between group ${
                isDark
                  ? 'bg-slate-900 border-white/10 text-white'
                  : 'bg-white border-slate-200/80 text-slate-900'
              }`}
            >
              <div className="relative h-64 overflow-hidden bg-slate-900">
                <img
                  src={route.coverImage}
                  alt={route.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-xl bg-slate-900/80 backdrop-blur-md text-white text-xs font-bold flex items-center gap-1.5 shadow">
                  <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                  <span>{route.durationDays} Gün / {route.durationDays - 1} Gece</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs text-white bg-gradient-to-t from-slate-950/90 via-slate-950/60 to-transparent p-3 rounded-xl backdrop-blur-xs">
                  <div className="flex items-center gap-1.5">
                    <Anchor className="w-4 h-4 text-cyan-400 shrink-0" />
                    <span className="font-semibold">{route.departurePort}</span>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
                    <span className="font-semibold">{route.arrivalPort}</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className={`text-xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{route.name}</h3>
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{route.description}</p>
                </div>

                <div className={`space-y-2 pt-2 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                    Öne Çıkan Koy & Duraklar
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {route.highlightBays.map((bay, idx) => (
                      <span
                        key={idx}
                        className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                          isDark
                            ? 'bg-slate-800 text-slate-200'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {bay}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-3">
                  <button
                    type="button"
                    onClick={() => onNavigate && onNavigate('/rezervasyon')}
                    className="w-full py-3 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-2 shadow-md hover:opacity-95 transition"
                    style={{ backgroundColor: accentColor, color: getOptimalTextColor(accentColor) }}
                  >
                    <Compass className="w-4 h-4" />
                    <span>Bu Rotayı Planla & Fiyat Al</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </section>
  );
};
